import type { BlockDef } from '../../prop-types.ts'
import { EMPTY_TEXT } from '../../text.ts'

/**
 * How to reach the business.
 *
 * ⚠️ NO CONTACT *FORM* IN THIS BLOCK, AND THAT IS A DELIBERATE ABSENCE RATHER THAN AN OMISSION. A
 * form means storing whatever a stranger types about themselves, which makes this business a data
 * processor under the Saudi PDPL — with a lawful basis, a retention period, a deletion route and a
 * disclosure to write. That is a product decision with legal consequences, not a field. Until it is
 * made, the block does what a small business actually wants: a WhatsApp link and a phone number that
 * dials.
 *
 * ⚠️ PHONE NUMBERS AND EMAIL ADDRESSES ARE `lit`-SHAPED IDENTIFIERS, and the renderer isolates their
 * direction. `+966 55 123 4567` inside an Arabic paragraph renders with the `+` at the wrong end
 * under the bidirectional algorithm unless it is isolated — a phone number that is subtly wrong on
 * every Arabic page is the kind of bug nobody reports and everybody notices.
 */
export const contact: BlockDef = {
  type: 'contact',
  v: 1,
  label: { en: 'Contact', ar: 'التواصل' },
  scope: 'page',
  description:
    'How to reach the business: a phone number that dials, a WhatsApp link, an email address, an address and the opening hours.',
  whenToUse: 'A Contact page, or the last section of a home page.',
  whenNotToUse: 'For branch addresses that should appear on every page — those belong in the Footer.',
  variants: [
    { value: 'stacked', label: { en: 'Stacked', ar: 'متتالٍ' } },
    { value: 'split', label: { en: 'Two columns', ar: 'عمودان' } },
  ],
  fields: [
    { key: 'heading', label: { en: 'Heading', ar: 'العنوان' }, type: { kind: 'text' }, default: EMPTY_TEXT },
    {
      key: 'phone',
      label: { en: 'Phone', ar: 'الهاتف' },
      type: { kind: 'text' },
      default: EMPTY_TEXT,
      hint: { en: 'In international form, e.g. +966 55 123 4567.', ar: 'بالصيغة الدولية، مثال ‎+966 55 123 4567‎.' },
    },
    { key: 'whatsapp', label: { en: 'WhatsApp', ar: 'واتساب' }, type: { kind: 'text' }, default: EMPTY_TEXT },
    { key: 'email', label: { en: 'Email', ar: 'البريد الإلكتروني' }, type: { kind: 'text' }, default: EMPTY_TEXT },
    { key: 'address', label: { en: 'Address', ar: 'العنوان' }, type: { kind: 'text' }, default: EMPTY_TEXT },
    {
      key: 'hours',
      label: { en: 'Opening hours', ar: 'ساعات العمل' },
      type: {
        kind: 'list',
        max: 7,
        item: [
          { key: 'days', label: { en: 'Days', ar: 'الأيام' }, type: { kind: 'text' }, default: EMPTY_TEXT },
          { key: 'time', label: { en: 'Hours', ar: 'الساعات' }, type: { kind: 'text' }, default: EMPTY_TEXT },
        ],
      },
      default: [],
    },
    {
      key: 'mapLink',
      label: { en: 'Map link', ar: 'رابط الخريطة' },
      // ⚠️ A LINK, NOT AN EMBED. An embedded map is an arbitrary `<iframe>` on a page we publish and
      // sign with our own certificate; the moment one block can embed a third-party document, every
      // block can, and moderation becomes a URL-reputation problem rather than a schema one. A link
      // opens the visitor's own maps app, which is what somebody standing outside wanted anyway.
      type: { kind: 'link' },
      default: { kind: 'none' },
    },
  ],
}
